import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Tabs from '../shared/Tabs';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from '../support/ProductSupportTab';
import '../AccountForm.css';
import './McpServicePage.css';

// MCP AS A SERVICE — THE HOTELS RUNNING IT (doc/MCP-AS-A-SERVICE.md §9.2), for the chain in the navbar (and only the
// hotel chosen there, when one is). The same list → detail shape as the other products: a row opens the hotel
// (McpHotelPage, /mcp-service/hotels/:hotelId — settings, assistants, email sender, terms, content, usage), and a hotel
// is added with the set-up wizard (McpHotelWizard, /mcp-service/new). It used to be every hotel of the chain on one
// screen, set up or not, each a full form (2026-10-19).
//
// A hotel is "set up" when it has an mcp_hotel_settings row (settings !== null). The chain's own settings — who may
// connect, limits, the kill switch, its sending domain — are on Chain Settings › MCP (McpChainSettings).
//
// The server decides who may change what (canEditChain / canEdit); the screen only follows it.

export default function McpServicePage() {
  const navigate = useNavigate();
  const { selectedAccount, selectedHotel } = useAccount();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useSupportTab('hotels');   // Hotels | Support

  const load = useCallback(async () => {
    if (!selectedAccount) return;
    setError(null);
    try { setData(await mcpServiceService.getAccount(selectedAccount)); } catch (e) { setError(apiErrorMessage(e)); }
  }, [selectedAccount]);
  useEffect(() => { setData(null); load(); }, [load]);

  const hotelId = selectedHotel && (selectedHotel.hotelid || selectedHotel.id || selectedHotel);
  const reachable = data ? data.hotels.filter(h => !hotelId || String(h.hotelId) === String(hotelId)) : [];
  const running = reachable.filter(h => h.settings);
  const addable = reachable.filter(h => !h.settings && h.canEdit);
  const credentialName = (h) => {
    const c = h.uapiCredentials.find(x => String(x.id) === String(h.settings.uapi_credential_id));
    return c ? c.name : null;
  };
  const chainPaused = data && data.chain && data.chain.service_status === 'PAUSED';

  return (
    <div className="account-form-container">
      <div className="account-form-header">
        <h1>MCP as a Service</h1>
        <p className="no-data-hint" style={{ margin: '0.35rem 0 0' }}>
          Let AI assistants search, quote and book your hotels — and answer questions from your own published information.
          The chain&rsquo;s own settings are on Chain Settings › MCP.
        </p>
      </div>

      {!selectedAccount && <div className="os-msg">Choose a chain in the navigation bar.</div>}
      {error && <div className="form-error">{error}</div>}
      {chainPaused && <div className="os-msg os-msg-error">The service is paused for the whole chain (Chain Settings › MCP) — every assistant is refused at every hotel below.</div>}

      {selectedAccount && <Tabs tabs={[{ key: 'hotels', label: 'Hotels', icon: <Building2 size={20} /> }, SUPPORT_TAB]} active={tab} onChange={setTab} />}

      {/* MCP's tickets for the hotel chosen in the navbar, else the whole chain. */}
      {selectedAccount && tab === 'support' && (
        <ProductSupportTab product="MCP" hotelId={hotelId || undefined} accountId={hotelId ? undefined : selectedAccount}
          noun={hotelId ? 'MCP as a Service at this hotel' : 'MCP as a Service in this chain'}
          context={() => ({ screen: 'mcp-service', ...(hotelId ? { hotelId } : { accountId: selectedAccount }) })} />
      )}

      {data && tab === 'hotels' && (
        <>
          <div className="hotels-header">
            <button type="button" className="btn-add-hotel" disabled={!addable.length}
                    title={addable.length ? undefined : 'Every hotel you can set up already runs the service'}
                    onClick={() => navigate('/mcp-service/new')}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Set up a hotel
            </button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>City</th>
                  <th>Status</th>
                  <th>Acts as</th>
                  <th>Booking</th>
                  <th>Assistants</th>
                  <th>Email sender</th>
                </tr>
              </thead>
              <tbody>
                {!running.length ? (
                  <tr><td colSpan="7" className="no-data">
                    <p>No hotels run MCP as a Service yet</p>
                    <p className="no-data-hint">{addable.length ? 'Set one up to let AI assistants search and book it' : 'There are no hotels here you can set up'}</p>
                  </td></tr>
                ) : running.map(h => (
                  <tr key={h.hotelId} className="clickable-row" onClick={() => navigate(`/mcp-service/hotels/${h.hotelId}`)}>
                    <td className="primary-text">{h.hotelName}</td>
                    <td>{h.city || '—'}</td>
                    <td><span className={`mcps-pill ${h.settings.service_status === 'ACTIVE' ? 'mcps-pill-verified' : 'mcps-pill-pending'}`}>
                      {h.settings.service_status === 'ACTIVE' ? 'Live' : 'Paused'}</span></td>
                    <td>{credentialName(h) || <span className="mcps-pill mcps-pill-failed">No credential</span>}</td>
                    <td>{h.settings.booking_enabled === 'Y' ? (h.settings.cancel_enabled === 'Y' ? 'Book and cancel' : 'Book') : 'Search only'}</td>
                    <td>{h.clients.filter(c => c.status === 'ACTIVE').length}</td>
                    <td>{h.senderDomain
                      ? <><span className="mcps-mono">{h.senderDomain.domain}</span> <span className={`mcps-pill mcps-pill-${h.senderDomain.status.toLowerCase()}`}>{h.senderDomain.status}</span></>
                      : <span className="mcps-meta">{data.senderDomain && data.senderDomain.status === 'VERIFIED' ? 'Chain’s domain' : 'hconnect'}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {selectedAccount && !data && !error && <p>Loading…</p>}
    </div>
  );
}
